/**
 * Lazy-loaded account detail for a selected row on the admin Parents page.
 *
 * The list row shows contact info and compact payment badges; once an admin has found the
 * family, this answers the follow-up questions without leaving the page: what they owe,
 * what they've paid, their BHCS Cash balance, what their children are enrolled in, and the
 * saved payment methods rendered exactly as the parent sees them on their Account page.
 *
 * Invoices hang off enrollments, not parents, so billing figures cover every invoice for
 * the linked students — shared with any co-parent on the same students.
 * Mounted only while the row is expanded.
 */
import { useQuery } from '@tanstack/react-query';
import { Repeat } from 'lucide-react';
import { supabase } from '../supabase';
import {
  formatDateDisplay,
  formatDateInBusinessTZ,
  getTodayDateStringInBusinessTZ,
} from '../../utils/date';
import { PaymentMethodRow } from '../../components/PaymentMethodRow';
import { describeSavedCard, type PaymentMethodDisplay } from '../../utils/paymentMethodDisplay';
import { AdminBrandTag } from './AdminBrandTag';
import { FosterCityResidentBadge } from './FosterCityResidentBadge';
import { isFosterCityZip } from '../../utils/eligibilityDiscounts';
import { money } from '../utils/money';

export interface ParentAccountDetailsParent {
  id: string;
  email: string | null;
  phone: string | null;
  address?: string | null;
  postal_code?: string | null;
  brand_preference?: string | null;
  created_at: string;
  updated_at: string;
  student_parents: {
    student: { id: string; first_name: string; last_name: string };
  }[];
}

interface CardRow {
  id: string;
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
}

interface BankRow {
  id: string;
  bank_name: string | null;
  last4: string | null;
  status: string | null;
}

interface PrefsRow {
  default_payment_method: 'card' | 'bank_account' | 'zelle' | null;
  autopay_enabled: boolean;
  default_bank_account_setup_intent_id: string | null;
}

interface EnrollmentRow {
  id: string;
  student_id: string;
  is_active: boolean;
  enrollment_start_date: string | null;
  enrollment_end_date: string | null;
  program: { name: string | null; brand_key: string | null } | null;
}

interface InvoiceRow {
  id: string;
  total_amount: number;
  amount_paid: number;
  due_date: string | null;
  status: string;
}

interface PaymentRow {
  amount: number;
  payment_method: string;
  payment_date: string | null;
  status: string;
  bank_account_setup_intent_id: string | null;
}

interface AccountDetails {
  cards: CardRow[];
  banks: BankRow[];
  prefs: PrefsRow | null;
  cashCents: number | null;
  enrollments: EnrollmentRow[];
  invoices: InvoiceRow[];
  payments: PaymentRow[];
}

// A payment that actually moved (or is moving) money — excludes failed/created attempts.
const SETTLED_PAYMENT_STATUSES = ['completed', 'partially_refunded', 'processing'];
const OPEN_INVOICE_STATUSES = ['pending', 'partial'];

async function fetchAccountDetails(parentId: string, studentIds: string[]): Promise<AccountDetails> {
  const [cardsRes, banksRes, prefsRes, accountRes, enrollmentsRes] = await Promise.all([
    supabase
      .from('card_payment_methods')
      .select('id, brand, last4, exp_month, exp_year')
      .eq('parent_id', parentId)
      .eq('is_active', true)
      .order('created_at', { ascending: false }),
    supabase
      .from('bank_account_setup_intents')
      .select('id, bank_name, last4, status')
      .eq('parent_id', parentId)
      .eq('is_active', true)
      .order('created_at', { ascending: false }),
    supabase
      .from('parent_payment_preferences')
      .select('default_payment_method, autopay_enabled, default_bank_account_setup_intent_id')
      .eq('parent_id', parentId)
      .maybeSingle(),
    supabase.from('balance_accounts').select('id').eq('parent_id', parentId).maybeSingle(),
    studentIds.length
      ? supabase
          .from('enrollments')
          .select('id, student_id, is_active, enrollment_start_date, enrollment_end_date, program:programs(name, brand_key)')
          .in('student_id', studentIds)
          .order('enrollment_start_date', { ascending: false })
      : Promise.resolve({ data: [], error: null }),
  ]);
  for (const res of [cardsRes, banksRes, prefsRes, accountRes, enrollmentsRes]) {
    if (res.error) throw res.error;
  }

  const enrollments = (enrollmentsRes.data ?? []) as unknown as EnrollmentRow[];
  const accountId = (accountRes.data as { id: string } | null)?.id ?? null;

  const [balanceRes, invoicesRes] = await Promise.all([
    accountId
      ? supabase.rpc('get_balance', { p_account_id: accountId })
      : Promise.resolve({ data: null, error: null }),
    enrollments.length
      ? supabase
          .from('invoices')
          .select('id, total_amount, amount_paid, due_date, status')
          .in('enrollment_id', enrollments.map((e) => e.id))
      : Promise.resolve({ data: [], error: null }),
  ]);
  if (balanceRes.error) throw balanceRes.error;
  if (invoicesRes.error) throw invoicesRes.error;

  const invoices = (invoicesRes.data ?? []) as InvoiceRow[];
  let payments: PaymentRow[] = [];
  if (invoices.length) {
    const { data, error } = await supabase
      .from('payments')
      .select('amount, payment_method, payment_date, status, bank_account_setup_intent_id')
      .in('invoice_id', invoices.map((i) => i.id))
      .in('status', SETTLED_PAYMENT_STATUSES)
      .order('payment_date', { ascending: false });
    if (error) throw error;
    payments = (data ?? []) as PaymentRow[];
  }

  const balanceRow = (balanceRes.data as { total_cents: number }[] | null)?.[0];
  return {
    cards: (cardsRes.data ?? []) as CardRow[],
    banks: (banksRes.data ?? []) as BankRow[],
    prefs: (prefsRes.data as PrefsRow | null) ?? null,
    cashCents: accountId ? (balanceRow?.total_cents ?? 0) : null,
    enrollments,
    invoices,
    payments,
  };
}

/** Saved methods in the parent Account page's shape, all active ones rather than just the latest. */
function buildPaymentMethods(d: AccountDetails): PaymentMethodDisplay[] {
  const pref = d.prefs?.default_payment_method ?? null;
  const pinnedBankId = d.prefs?.default_bank_account_setup_intent_id ?? null;
  const verifiedBanks = d.banks.filter((b) => b.status === 'succeeded');
  const defaultBankId = pref === 'bank_account' ? (pinnedBankId ?? verifiedBanks[0]?.id ?? null) : null;
  // Payments are newest first, so the first match is the last use.
  const lastUsed = (match: (p: PaymentRow) => boolean) => d.payments.find(match)?.payment_date ?? null;

  const methods: PaymentMethodDisplay[] = [];
  d.banks.forEach((b) => {
    methods.push({
      id: b.id,
      type: 'bank_account',
      name: `${b.bank_name ?? 'Bank'} •••• ${b.last4 ?? '••••'}`,
      isDefault: b.id === defaultBankId,
      bankVerificationStatus: b.status === 'succeeded' ? 'verified' : 'pending',
      lastUsed: lastUsed((p) => p.payment_method === 'ach' && p.bank_account_setup_intent_id === b.id),
    });
  });
  d.cards.forEach((c, i) => {
    methods.push({
      id: c.id,
      type: 'card',
      ...describeSavedCard(c),
      // Charges use the most recent active card, so that one carries the default.
      isDefault: pref === 'card' && i === 0,
      lastUsed: i === 0 ? lastUsed((p) => p.payment_method === 'card') : null,
    });
  });
  const zelleLastUsed = lastUsed((p) => p.payment_method === 'zelle');
  if (pref === 'zelle' || zelleLastUsed) {
    methods.push({
      id: 'zelle',
      type: 'zelle',
      name: 'Zelle',
      isDefault: pref === 'zelle',
      lastUsed: zelleLastUsed,
    });
  }
  return methods;
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-xs">
      <span className="w-24 shrink-0 font-medium text-gray-500">{label}</span>
      <span className="min-w-0 flex-1 break-words text-gray-800">{children}</span>
    </div>
  );
}

function Stat({ label, value, sub, tone = 'default' }: {
  label: string;
  value: string;
  sub?: string | null;
  tone?: 'default' | 'warn' | 'good';
}) {
  const valueCls = tone === 'warn' ? 'text-red-700' : tone === 'good' ? 'text-green-700' : 'text-gray-900';
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-base font-semibold ${valueCls}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-500">{sub}</div>}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-700">{children}</div>;
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  ach: 'bank',
  card: 'card',
  zelle: 'Zelle',
  bhcs_cash: 'BHCS Cash',
  check: 'check',
};

export function ParentAccountDetails({ parent }: { parent: ParentAccountDetailsParent }) {
  const students = parent.student_parents?.map((sp) => sp.student) ?? [];
  const studentIds = students.map((s) => s.id);
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-parent-account', parent.id, studentIds],
    queryFn: () => fetchAccountDetails(parent.id, studentIds),
  });

  const accountFacts = (
    <div className="space-y-1">
      <SectionTitle>Account</SectionTitle>
      <Fact label="Parent ID"><span className="font-mono text-[11px]">{parent.id}</span></Fact>
      <Fact label="Email">{parent.email || '—'}</Fact>
      <Fact label="Phone">{parent.phone || '—'}</Fact>
      <Fact label="Address">
        {[parent.address, parent.postal_code].filter(Boolean).join(', ') || '—'}
        {isFosterCityZip(parent.postal_code) && <FosterCityResidentBadge className="ml-2" />}
      </Fact>
      {parent.brand_preference && (
        <Fact label="Brand"><AdminBrandTag brand={parent.brand_preference} showDefault /></Fact>
      )}
      <Fact label="Joined">{formatDateInBusinessTZ(parent.created_at)}</Fact>
      <Fact label="Last updated">{formatDateInBusinessTZ(parent.updated_at)}</Fact>
    </div>
  );

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {accountFacts}
        <div className="text-xs text-gray-500">Loading account details…</div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {accountFacts}
        <div className="text-xs text-red-600">Failed to load account details</div>
      </div>
    );
  }

  const today = getTodayDateStringInBusinessTZ();
  const openInvoices = data.invoices.filter(
    (i) => OPEN_INVOICE_STATUSES.includes(i.status) && Number(i.total_amount) - Number(i.amount_paid) > 0,
  );
  const owed = (i: InvoiceRow) => Number(i.total_amount) - Number(i.amount_paid);
  const outstanding = openInvoices.reduce((sum, i) => sum + owed(i), 0);
  const overdue = openInvoices.filter((i) => i.due_date && i.due_date < today);
  const overdueAmount = overdue.reduce((sum, i) => sum + owed(i), 0);
  // amount_paid is already net of refunds.
  const collected = data.invoices
    .filter((i) => i.status !== 'voided')
    .reduce((sum, i) => sum + Number(i.amount_paid), 0);
  const lastPayment = data.payments[0] ?? null;

  const currentEnrollments = data.enrollments.filter(
    (e) => e.is_active && (!e.enrollment_end_date || e.enrollment_end_date >= today),
  );
  const pastCount = data.enrollments.length - currentEnrollments.length;
  const studentName = new Map(students.map((s) => [s.id, `${s.first_name} ${s.last_name}`]));

  const methods = buildPaymentMethods(data);
  const autopayOn = data.prefs?.autopay_enabled ?? false;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        {accountFacts}
        <div>
          <SectionTitle>Billing</SectionTitle>
          <div className="grid grid-cols-2 gap-2">
            <Stat
              label="Outstanding"
              value={money(outstanding)}
              sub={`${openInvoices.length} open invoice${openInvoices.length === 1 ? '' : 's'}`}
              tone={outstanding > 0 ? 'warn' : 'default'}
            />
            <Stat
              label="Overdue"
              value={money(overdueAmount)}
              sub={overdue.length ? `${overdue.length} past due` : 'None past due'}
              tone={overdueAmount > 0 ? 'warn' : 'default'}
            />
            <Stat
              label="Collected"
              value={money(collected)}
              sub={`${data.invoices.length} invoice${data.invoices.length === 1 ? '' : 's'} total`}
            />
            <Stat
              label="BHCS Cash"
              value={data.cashCents === null ? '—' : money(data.cashCents / 100)}
              sub={data.cashCents === null ? 'No account' : null}
              tone={data.cashCents ? 'good' : 'default'}
            />
          </div>
          <div className="mt-2 text-xs text-gray-600">
            {lastPayment ? (
              <>
                Last payment {money(lastPayment.amount)} via{' '}
                {PAYMENT_METHOD_LABELS[lastPayment.payment_method] ?? lastPayment.payment_method}
                {lastPayment.payment_date && <> on {formatDateInBusinessTZ(lastPayment.payment_date)}</>}
                {lastPayment.status === 'processing' && <span className="text-amber-700"> (processing)</span>}
              </>
            ) : (
              'No payments yet'
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <SectionTitle>
            Current enrollments
            {pastCount > 0 && (
              <span className="ml-1 font-normal normal-case tracking-normal text-gray-500">
                · {pastCount} past/inactive
              </span>
            )}
          </SectionTitle>
          {currentEnrollments.length === 0 ? (
            <div className="text-xs text-gray-500">None</div>
          ) : (
            <ul className="space-y-1">
              {currentEnrollments.map((e) => (
                <li key={e.id} className="flex flex-wrap items-center gap-x-2 text-xs">
                  <span className="font-medium text-gray-900">{studentName.get(e.student_id) ?? 'Student'}</span>
                  <span className="text-gray-700">{e.program?.name ?? 'Program'}</span>
                  <AdminBrandTag brand={e.program?.brand_key} />
                  {e.enrollment_start_date && (
                    <span className="text-gray-500">
                      {formatDateDisplay(e.enrollment_start_date)}
                      {e.enrollment_end_date ? ` – ${formatDateDisplay(e.enrollment_end_date)}` : ''}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <SectionTitle>Payment methods</SectionTitle>
          <div className="mb-2 flex items-center gap-1.5 text-xs text-gray-700">
            <Repeat className="h-3.5 w-3.5 text-gray-500" />
            Auto Pay{' '}
            <span
              className={`rounded-full px-2 text-[11px] font-semibold leading-5 ${
                autopayOn ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}
            >
              {autopayOn ? 'On' : 'Off'}
            </span>
          </div>
          {methods.length === 0 ? (
            <div className="text-xs text-gray-500">No saved payment methods</div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 divide-y divide-gray-100">
              {methods.map((m) => (
                <PaymentMethodRow key={m.id} method={m} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
